import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useUserMutations } from '@klear/ui/hooks/use-user'
import { userSchemaToCreateWire } from '@klear/ui/lib/user-wire'
import { EMPTY_USER, validateUser, type UserSchema, type UserFormErrors } from '@klear/ui/lib/user-form-schema'
import {
  PERMISSION_ENTITY_USER,
  PERMISSION_ACTION_ADD,
  ROLE_TRADER_USER,
  TRADER_ROLES_THAT_CAN_MANAGE_ORGAN_USERS,
  ROUTE_TEAM,
  buildNavPathTrader
} from '@klear/constants'
import isEmpty from 'lodash/isEmpty'
import TeamUserForm from '@/app/organization/team-user-form'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export default function TeamUserAdd() {
  useDocumentTitle('Add Team Member')
  const { user } = useAuth()
  const { traderId } = useParams()
  const navigate = useNavigate()
  const { createOrInviteUser } = useUserMutations()

  const numericTraderId = parseInt(traderId!)
  const teamPath = `${buildNavPathTrader(traderId!)}/${ROUTE_TEAM}`

  const [values, setValues] = useState<UserSchema>({
    ...EMPTY_USER,
    roles: [{ role: ROLE_TRADER_USER, traderId: numericTraderId }]
  })
  const [errors, setErrors] = useState<UserFormErrors>({})
  const [submitting, setSubmitting] = useState(false)

  const canManage =
    user.hasKlearRole || user.roles?.some((r) => TRADER_ROLES_THAT_CAN_MANAGE_ORGAN_USERS.includes(r.role)) || false

  if (!canManage || !user.hasPermission(PERMISSION_ENTITY_USER, PERMISSION_ACTION_ADD)) {
    return null
  }

  const onChange = (patch: Partial<UserSchema>) => setValues((prev) => ({ ...prev, ...patch }))

  const handleSubmit = async () => {
    const nextErrors: UserFormErrors = validateUser(values)
    if (!values.roles?.[0]?.role) nextErrors.roles = 'User must have one role'

    setErrors(nextErrors)
    if (!isEmpty(nextErrors)) return

    setSubmitting(true)
    try {
      await createOrInviteUser(userSchemaToCreateWire(values))
      navigate(teamPath)
    } catch {
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <TeamUserForm
      values={values}
      errors={errors}
      onChange={onChange}
      isNewUser={true}
      onSubmit={handleSubmit}
      cancelPath={teamPath}
      title="Add Team Member"
      submitting={submitting}
    />
  )
}
